import { getAccessToken } from './auth-store'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8789'

export interface ApiError {
  error: { code: string; message: string }
}

// Auth types
export interface LoginRequest {
  email: string
  password: string
}
export interface SignupRequest {
  email: string
  password: string
  name?: string
  passwordConfirmation: string
}
export interface AuthResponse {
  data: {
    accessToken: string
    refreshToken: string
    expiresIn: number
    user: { id: string; email: string; name: string | null }
  }
}
export interface PasswordResetRequest {
  email: string
}
export interface ResetPasswordRequest {
  token: string
  password: string
}
export interface VerifyEmailRequest {
  token: string
}

// Testimonial types
export type TestimonialStatus = 'pending' | 'approved' | 'rejected'

export interface Testimonial {
  id: string
  collectionPageId: string
  authorName: string
  authorEmail: string | null
  authorCompany: string | null
  authorRole: string | null
  authorPhotoUrl: string | null
  content: string
  rating: number | null
  status: TestimonialStatus
  isFavorite: boolean
  source: string
  createdAt: string
  updatedAt: string
}

export interface TestimonialListResponse {
  data: Testimonial[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
}

// Collection Page types
export interface CollectionPage {
  id: string
  name: string
  slug: string
  headline: string
  description: string | null
  brandColor: string
  thankYouMessage: string
  collectStarRating: boolean
  collectPhoto: boolean
  collectCompany: boolean
  collectRole: boolean
  customQuestions: string[]
  createdAt: string
  updatedAt: string
}

export interface CreateCollectionPageRequest {
  name: string
  slug?: string
  headline: string
  description?: string
  brandColor?: string
  thankYouMessage?: string
  customQuestions?: string[]
  collectStarRating?: boolean
  collectPhoto?: boolean
  collectCompany?: boolean
  collectRole?: boolean
}

export interface UpdateCollectionPageRequest {
  name?: string
  headline?: string
  description?: string
  brandColor?: string
  thankYouMessage?: string
  customQuestions?: string[]
  collectStarRating?: boolean
  collectPhoto?: boolean
  collectCompany?: boolean
  collectRole?: boolean
}

// Widget types
export type WidgetLayout = 'grid' | 'carousel' | 'list' | 'masonry' | 'marquee' | 'badge'

export interface Widget {
  id: string
  name: string
  layout: WidgetLayout
  collectionPageIds: string[]
  maxTestimonials: number
  displayOrder: string
  showRatings: boolean
  showPhotos: boolean
  showCompanyRole: boolean
  showDate: boolean
  backgroundColor: string
  textColor: string
  accentColor: string
  borderRadius: number
  fontFamily: string
  cardStyle: string
  createdAt: string
  updatedAt: string
}

export interface CreateWidgetRequest {
  name: string
  layout: WidgetLayout
  collectionPageIds: string[]
  maxTestimonials?: number
  displayOrder?: string
  showRatings?: boolean
  showPhotos?: boolean
  showCompanyRole?: boolean
  showDate?: boolean
  backgroundColor?: string
  textColor?: string
  accentColor?: string
  borderRadius?: number
  fontFamily?: string
  cardStyle?: string
}

export interface UpdateWidgetRequest {
  name?: string
  layout?: WidgetLayout
  collectionPageIds?: string[]
  maxTestimonials?: number
  displayOrder?: string
  showRatings?: boolean
  showPhotos?: boolean
  showCompanyRole?: boolean
  showDate?: boolean
  backgroundColor?: string
  textColor?: string
  accentColor?: string
  borderRadius?: number
  fontFamily?: string
  cardStyle?: string
}

// Analytics types
export interface DashboardStats {
  totalTestimonials: number
  pendingTestimonials: number
  approvedTestimonials: number
  totalWidgets: number
  totalCollectionPages: number
  widgetImpressions: number
  collectionPageVisits: number
}

// Billing types
export interface SubscriptionInfo {
  plan: 'free' | 'pro' | 'business'
  status: string
}

export interface UsageInfo {
  testimonials: { used: number; limit: number }
  widgets: { used: number; limit: number }
  collectionPages: { used: number; limit: number }
}

// Settings types
export interface UserProfile {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  timezone: string | null
  emailVerified: boolean
  createdAt: string
}

export interface NotificationPreferences {
  newTestimonial: boolean
  weeklyDigest: boolean
  planChanges: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (requireAuth) {
      const token = getAccessToken()
      if (token) {
        ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
      }
    }

    let response: Response
    try {
      response = await fetch(url, { ...options, headers })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      if (message.includes('fetch') || message.includes('network') || message.includes('Failed')) {
        throw new Error(
          `Unable to connect to the API server at ${this.baseUrl}. Please check that the server is running and accessible.`
        )
      }
      throw new Error(`Network error: ${message}`)
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      if (!response.ok) {
        throw new Error(
          `Server error (${response.status}): ${response.statusText || 'Unknown error'}`
        )
      }
      throw new Error('Invalid response from server')
    }

    if (!response.ok) {
      const errorData = data as ApiError
      const errorMessage =
        errorData.error?.message ?? `Request failed with status ${response.status}`
      throw new Error(errorMessage)
    }

    return data as T
  }

  // Auth
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async signup(data: SignupRequest): Promise<{ data: { userId: string; email: string } }> {
    return this.request('/api/auth/register', { method: 'POST', body: JSON.stringify(data) })
  }

  async refreshToken(
    refreshToken: string
  ): Promise<{ data: { accessToken: string; refreshToken: string } }> {
    return this.request('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  }

  async requestPasswordReset(data: PasswordResetRequest): Promise<{ data: { message: string } }> {
    return this.request('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) })
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ data: { message: string } }> {
    return this.request('/api/auth/reset-password', { method: 'POST', body: JSON.stringify(data) })
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<{ data: { message: string } }> {
    return this.request('/api/auth/verify-email', { method: 'POST', body: JSON.stringify(data) })
  }

  // Testimonials
  async listTestimonials(params?: {
    page?: number
    limit?: number
    status?: TestimonialStatus
    search?: string
    favoriteOnly?: boolean
  }): Promise<TestimonialListResponse> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.limit !== undefined) query.set('limit', params.limit.toString())
    if (params?.status) query.set('status', params.status)
    if (params?.search) query.set('search', params.search)
    if (params?.favoriteOnly) query.set('favoriteOnly', 'true')
    const qs = query.toString()
    return this.request<TestimonialListResponse>(
      qs ? `/api/testimonials?${qs}` : '/api/testimonials',
      {},
      true
    )
  }

  async getTestimonial(id: string): Promise<{ data: Testimonial }> {
    return this.request(`/api/testimonials/${id}`, {}, true)
  }

  async approveTestimonial(id: string): Promise<{ data: Testimonial }> {
    return this.request(`/api/testimonials/${id}/approve`, { method: 'PATCH' }, true)
  }

  async rejectTestimonial(id: string): Promise<{ data: Testimonial }> {
    return this.request(`/api/testimonials/${id}/reject`, { method: 'PATCH' }, true)
  }

  async favoriteTestimonial(id: string): Promise<{ data: Testimonial }> {
    return this.request(`/api/testimonials/${id}/favorite`, { method: 'PATCH' }, true)
  }

  async deleteTestimonial(id: string): Promise<{ data: { message: string } }> {
    return this.request(`/api/testimonials/${id}`, { method: 'DELETE' }, true)
  }

  async bulkApproveTestimonials(ids: string[]): Promise<{ data: { updated: number } }> {
    return this.request(
      '/api/testimonials/bulk-approve',
      { method: 'POST', body: JSON.stringify({ ids }) },
      true
    )
  }

  async bulkRejectTestimonials(ids: string[]): Promise<{ data: { updated: number } }> {
    return this.request(
      '/api/testimonials/bulk-reject',
      { method: 'POST', body: JSON.stringify({ ids }) },
      true
    )
  }

  // Collection Pages
  async listCollectionPages(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<CollectionPage>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.limit !== undefined) query.set('limit', params.limit.toString())
    const qs = query.toString()
    return this.request(qs ? `/api/collection-pages?${qs}` : '/api/collection-pages', {}, true)
  }

  async getCollectionPage(id: string): Promise<{ data: CollectionPage }> {
    return this.request(`/api/collection-pages/${id}`, {}, true)
  }

  async createCollectionPage(data: CreateCollectionPageRequest): Promise<{ data: CollectionPage }> {
    return this.request(
      '/api/collection-pages',
      { method: 'POST', body: JSON.stringify(data) },
      true
    )
  }

  async updateCollectionPage(
    id: string,
    data: UpdateCollectionPageRequest
  ): Promise<{ data: CollectionPage }> {
    return this.request(
      `/api/collection-pages/${id}`,
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async deleteCollectionPage(id: string): Promise<{ data: { message: string } }> {
    return this.request(`/api/collection-pages/${id}`, { method: 'DELETE' }, true)
  }

  async getCollectionPageStats(id: string): Promise<{ data: Record<string, unknown> }> {
    return this.request(`/api/collection-pages/${id}/stats`, {}, true)
  }

  // Widgets
  async listWidgets(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<Widget>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.set('page', params.page.toString())
    if (params?.limit !== undefined) query.set('limit', params.limit.toString())
    const qs = query.toString()
    return this.request(qs ? `/api/widgets?${qs}` : '/api/widgets', {}, true)
  }

  async getWidget(id: string): Promise<{ data: Widget }> {
    return this.request(`/api/widgets/${id}`, {}, true)
  }

  async createWidget(data: CreateWidgetRequest): Promise<{ data: Widget }> {
    return this.request('/api/widgets', { method: 'POST', body: JSON.stringify(data) }, true)
  }

  async updateWidget(id: string, data: UpdateWidgetRequest): Promise<{ data: Widget }> {
    return this.request(`/api/widgets/${id}`, { method: 'PUT', body: JSON.stringify(data) }, true)
  }

  async deleteWidget(id: string): Promise<{ data: { message: string } }> {
    return this.request(`/api/widgets/${id}`, { method: 'DELETE' }, true)
  }

  async getWidgetEmbedCode(id: string): Promise<{ data: { embedCode: string } }> {
    return this.request(`/api/widgets/${id}/embed-code`, {}, true)
  }

  // Analytics
  async getDashboardStats(): Promise<{ data: DashboardStats }> {
    return this.request('/api/analytics/overview', {}, true)
  }

  // Billing
  async getSubscription(): Promise<{ data: SubscriptionInfo }> {
    return this.request('/api/billing/subscription', {}, true)
  }

  async getUsage(): Promise<{ data: UsageInfo }> {
    return this.request('/api/billing/usage', {}, true)
  }

  async createCheckout(variantId: string): Promise<{ data: { url: string } }> {
    return this.request(
      '/api/billing/checkout',
      { method: 'POST', body: JSON.stringify({ variantId }) },
      true
    )
  }

  // Settings
  async getProfile(): Promise<{ data: UserProfile }> {
    return this.request('/api/settings/profile', {}, true)
  }

  async updateProfile(data: { name?: string; timezone?: string }): Promise<{ data: UserProfile }> {
    return this.request(
      '/api/settings/profile',
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async changePassword(data: {
    currentPassword: string
    newPassword: string
  }): Promise<{ data: { message: string } }> {
    return this.request(
      '/api/settings/password',
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async getNotificationPreferences(): Promise<{ data: NotificationPreferences }> {
    return this.request('/api/settings/notifications', {}, true)
  }

  async updateNotificationPreferences(
    data: Partial<NotificationPreferences>
  ): Promise<{ data: NotificationPreferences }> {
    return this.request(
      '/api/settings/notifications',
      { method: 'PUT', body: JSON.stringify(data) },
      true
    )
  }

  async deleteAccount(): Promise<{ data: { message: string } }> {
    return this.request('/api/settings/account', { method: 'DELETE' }, true)
  }
}

export const api = new ApiClient(API_BASE_URL)
